import React, { useCallback, useMemo, useRef, useState } from 'react';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import TableCell from '@mui/material/TableCell';
import NoResults from '@Components/NoResults/NoResults';
import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import styled from 'styled-components';
import MainTable, { MainTableRow } from '@Components/MainTable/MainTable';
import { TableHeader } from '@Features/generics/generics.type';
import { FlexLayout } from '@Styled/utilities';
import { useTypedSelector } from '@Features/store';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import { useDispatch } from 'react-redux';
import {
	Icon,
	RadioGroup,
	Radio,
	FormControlLabel,
	Button,
} from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import { CSVLink } from 'react-csv';
import { Typography } from '@eduact/ed-system';
import { describeUploadError } from '@RichTextEditor/uploadErrors';
import { ALLOWED_IMAGE_ACCEPT } from '@RichTextEditor/imageUtils';
// Per-app modules through the @Pages alias: the instructor app re-exports
// this file, and the alias resolves into ITS tree (its slice, its api base).
import { testImportsGetAll } from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseContent/Pages/Test/Pages/TestImports/Slice/Imports.slice';
import {
	TestImportType,
	importsHandlerMap,
} from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseContent/Pages/Test/Pages/TestImports/Types/Imports.types';
// Relative on purpose: pure helpers shared by both builds.
import {
	QuestionCsv,
	buildQuestionCsvFile,
	downloadQuestionTemplate,
	parseQuestionCsv,
} from './Helpers/questionCsv';
import {
	checkQuestionImages,
	rewriteImageCells,
	uploadQuestionImages,
} from './Helpers/questionImages';
import QuestionImportCheck from './Components/QuestionImportCheck';

/**
 * The test page's Imports tab — one implementation for both portals (the
 * instructor app re-exports it).
 *
 * Questions CSV columns: content, score, type (mcq | mrq | essay), model,
 * answer_mode (essay: text | attachment | both), image, feedback,
 * ch1/correct1… An `image` cell is a link (fetched by the server) or a file
 * from the teacher's PC: the teacher selects those files here, they are
 * matched by name, uploaded, and the cell is rewritten to the stored URL
 * before the sheet is sent. The server puts the image above the question.
 */

const tableHeaders: Array<TableHeader> = [
	{
		displayName: 'ID',
		fieldName: 'id',
		width: '10%',
	},
	{
		displayName: 'Status',
		fieldName: 'status',
		width: '10%',
	},
	{
		displayName: 'Imported',
		fieldName: 'imported_count',
		width: '10%',
	},
	{
		displayName: 'Failed',
		fieldName: 'failed_count',
		width: '10%',
	},
	{
		displayName: 'Description',
		fieldName: 'description',
		width: '25%',
	},
	{
		displayName: 'Import date',
		fieldName: 'created_at',
		width: '20%',
	},
	{
		displayName: 'Data',
		fieldName: 'imported_data',
		width: '20%',
		canSort: false,
	},
	// {
	// 	displayName: 'Failed data',
	// 	fieldName: 'failed_data',
	// 	width: '15%',
	// },
];

const safeParse = (value?: string | null): any[] => {
	try {
		const parsed = JSON.parse(value ?? 'null');
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

const ImportsTable: React.FC = () => {
	const { meta, filters, fetchAll } = useTypedSelector(
		(state) => state.TestImports
	);
	const [fileValue, setFileValue] = useState<File | null>(null);
	const [questionCsv, setQuestionCsv] = useState<QuestionCsv | null>(null);
	const [pickedImages, setPickedImages] = useState<File[]>([]);
	const [action, setAction] = useState<TestImportType>('attempts');
	const [importing, setImporting] = useState(false);
	const [progress, setProgress] = useState<{
		done: number;
		total: number;
	} | null>(null);

	const [refetch, setRefetch] = useState(false);
	const dispatch = useDispatch();
	const { displaySnackbar } = useSnackbar();
	const inputFileRef = useRef<HTMLInputElement>(null);
	const imagesRef = useRef<HTMLInputElement>(null);
	const folderRef = useRef<HTMLInputElement>(null);

	const { currentEntity } = useTypedSelector((s) => s.Tests);

	const openPicker = (ref: React.RefObject<HTMLInputElement>) => {
		if (ref.current) {
			ref.current.value = '';
			ref.current.click();
		}
	};

	const clearFile = () => {
		setFileValue(null);
		setQuestionCsv(null);
		setPickedImages([]);
	};

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.item(0);
		if (!file) return;
		// By extension: Excel on Windows reports CSVs as application/vnd.ms-excel.
		if (!file.name.toLowerCase().endsWith('.csv')) {
			displaySnackbar('error', 'Accept only csv files');
			clearFile();
			return;
		}
		setFileValue(file);
		setPickedImages([]);
		try {
			setQuestionCsv(await parseQuestionCsv(file));
		} catch {
			// Unreadable here — the server's parser still reports the real error.
			setQuestionCsv(null);
		}
	};

	const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files ?? []).filter((file) =>
			/\.(png|jpe?g|gif|webp)$/i.test(file.name)
		);
		// Adding to the selection lets a teacher pick from more than one folder.
		setPickedImages((current) => {
			const seen = new Set(
				current.map((file) => `${file.name}|${file.size}|${file.lastModified}`)
			);
			return [
				...current,
				...files.filter(
					(file) => !seen.has(`${file.name}|${file.size}|${file.lastModified}`)
				),
			];
		});
	};

	const imageCheck = useMemo(
		() =>
			action === 'questions' && questionCsv
				? checkQuestionImages(questionCsv, pickedImages)
				: null,
		[action, questionCsv, pickedImages]
	);
	const blocked =
		!!imageCheck &&
		(imageCheck.duplicates.length > 0 || imageCheck.invalid.length > 0);

	const importFile = async () => {
		if (!fileValue) {
			displaySnackbar('error', 'Please upload file');
			return;
		}
		if (!currentEntity || !currentEntity.id) return;
		setImporting(true);
		try {
			let file: File = fileValue;
			// Only a sheet with local images is rewritten; every other file
			// goes up exactly as the teacher saved it.
			if (imageCheck && questionCsv && imageCheck.matched.size > 0) {
				let urls: Map<string, string>;
				try {
					urls = await uploadQuestionImages(imageCheck.matched, (done, total) =>
						setProgress({ done, total })
					);
				} catch (error) {
					displaySnackbar('error', describeUploadError(error));
					return;
				}
				file = buildQuestionCsvFile(
					rewriteImageCells(questionCsv, urls),
					fileValue.name
				);
			}
			const formData = new FormData();
			formData.append('file', file);
			formData.append('test_id', currentEntity.id.toString());
			await importsHandlerMap[action](formData);
			displaySnackbar(
				'success',
				'Import queued — refresh in a moment to see the result'
			);
			clearFile();
			setRefetch(true);
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message || error?.message || 'Import failed'
			);
		} finally {
			setImporting(false);
			setProgress(null);
		}
	};

	const onPageRequestCallback = useCallback(
		({
			activePage,
			endDate,
			filters,
			query,
			sortBy,
			startDate,
			perPage,
		}: onPageChangeRequestMeta) => {
			if (currentEntity.id)
				dispatch(
					testImportsGetAll({
						filters,
						page: activePage,
						perPage: perPage ?? 10,
						query,
						sortBy,
						from: startDate,
						to: endDate,
						test_id: currentEntity.id,
					})
				);
			setRefetch(false);
		},
		[currentEntity.id]
	);


	return (
		<>
			<FlexLayout flexDirection="column" mb="40px">
				<input
					type="file"
					accept=".csv"
					hidden
					onChange={handleFileChange}
					ref={inputFileRef}
				/>
				<input
					type="file"
					accept={ALLOWED_IMAGE_ACCEPT}
					multiple
					hidden
					onChange={handleImagesChange}
					ref={imagesRef}
				/>
				<input
					type="file"
					hidden
					onChange={handleImagesChange}
					ref={folderRef}
					// Folder picker; not in React's <input> typings, passed through as a DOM attribute.
					{...({ webkitdirectory: '' } as Record<string, string>)}
				/>
				<FlexLayout gridGap="1rem" alignItems="center">
					<UploadButton
						onClick={() => openPicker(inputFileRef)}
						startIcon={<Icon>upload_file</Icon>}
						variant="outlined"
						color="primary"
						disabled={importing}
					>
						Upload File
					</UploadButton>
					{action === 'questions' && (
						<UploadButton
							onClick={downloadQuestionTemplate}
							startIcon={<Icon>download</Icon>}
							color="primary"
						>
							Questions template
						</UploadButton>
					)}
				</FlexLayout>
				{fileValue && (
					<FlexLayout
						minWidth="250px"
						width="fit-content"
						marginLeft="10px"
						justifyContent="space-between"
					>
						<p style={{ fontSize: '18px' }}>{fileValue.name}</p>
						<EdIcon
							style={{
								color: '#f74343',
								marginLeft: '10px',
								marginTop: '-5px',
								cursor: 'pointer',
							}}
							fontSize="small"
							onClick={() => !importing && clearFile()}
						>
							<Icon>delete</Icon>
						</EdIcon>
					</FlexLayout>
				)}
			</FlexLayout>
			{imageCheck && questionCsv && (
				<QuestionImportCheck
					check={imageCheck}
					rows={questionCsv.rows.length}
					pickedCount={pickedImages.length}
					onPickFiles={() => openPicker(imagesRef)}
					onPickFolder={() => openPicker(folderRef)}
					onClearPicked={() => setPickedImages([])}
					progress={progress}
				/>
			)}
			<FlexLayout mb="50px">
				<RadioGroup
					row
					value={action}
					style={{ flex: 1.5 }}
					onChange={(event, value) => {
						setAction(value as TestImportType);
					}}
				>
					<FormControlLabel
						value="questions"
						label="Test Questions"
						control={<Radio />}
					/>
					<FormControlLabel
						value="attempts"
						label="Test Attempts"
						control={<Radio />}
					/>
				</RadioGroup>
				<FlexLayout justifyContent="flex-end" gridGap="1rem">
					<RoundButton
						onClick={() => setRefetch(true)}
						startIcon={<EdIcon>refresh</EdIcon>}
						variant="outlined"
					>
						Refresh
					</RoundButton>
					<RoundButton
						onClick={importFile}
						sx={{ marginRight: '1rem' }}
						startIcon={<EdIcon>redo</EdIcon>}
						variant="contained"
						disabled={importing || blocked}
					>
						{importing ? 'Importing…' : 'Import'}
					</RoundButton>
				</FlexLayout>
			</FlexLayout>

			<MainTable
				tableFilters={filters}
				refetch={refetch}
				tableHeads={tableHeaders}
				totalPages={meta?.last_page ?? 0}
				onPageChangeRequest={onPageRequestCallback}
				RowsperPage={meta?.per_page}
				pageNum={meta?.current_page}
				$hasActions
				total={meta?.total}
				renderItems={() => {
					if (fetchAll.length === 0)
						return (
							<MainTableRow>
								<TableCell colSpan={tableHeaders.length}>
									<NoResults />
								</TableCell>
							</MainTableRow>
						);
					return fetchAll.map((importValue) => {
						return (
							<MainTableRow key={importValue?.id} hover>
								<TableCell>
									<Typography fontSize={'12px'}>
										{importValue?.id || '-'}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography fontSize={'12px'}>
										{importValue?.status || '-'}
									</Typography>
								</TableCell>
								<TableCell>{importValue?.imported_count || '-'}</TableCell>
								<TableCell>{importValue?.failed_count || '-'}</TableCell>
								<TableCell
									style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
								>
									{importValue?.description || '-'}
								</TableCell>
								<TableCell>
									<DateTimeFormatter date={importValue.created_at} />
								</TableCell>
								<TableCell>
									<FlexLayout gridGap={'1rem'} justifyContent={'center'}>
										{safeParse(importValue.imported_data).length > 0 ? (
											<CSVLink
												data={safeParse(importValue.imported_data)}
												filename={'ImportedData' + importValue.id + '.csv'}
											>
												<StyledImportDataButton ImportedFlag={true}>
													<EdIcon>download</EdIcon>
												</StyledImportDataButton>
											</CSVLink>
										) : (
											<StyledImportDataButton
												ImportedFlag={true}
												onClick={() => {
													displaySnackbar('error', 'No data to download');
												}}
											>
												<EdIcon>download</EdIcon>
											</StyledImportDataButton>
										)}
										{safeParse(importValue.failed_data).length > 0 ? (
											<CSVLink
												data={safeParse(importValue.failed_data)}
												filename={'FailedData' + importValue.id + '.csv'}
											>
												<StyledImportDataButton ImportedFlag={false}>
													<EdIcon>download</EdIcon>
												</StyledImportDataButton>
											</CSVLink>
										) : (
											<StyledImportDataButton
												ImportedFlag={false}
												onClick={() => {
													displaySnackbar('error', 'No data to download');
												}}
											>
												<EdIcon>download</EdIcon>
											</StyledImportDataButton>
										)}
									</FlexLayout>
								</TableCell>
							</MainTableRow>
						);
					});
				}}
			/>
		</>
	);
};

const StyledImportDataButton = styled.button<{ ImportedFlag: boolean }>`
	width: 34px;
	height: 34px;
	border-radius: 6px;
	padding-top: 5px;
	border: none;
	cursor: pointer;
	background-color: ${(props) => (props.ImportedFlag ? '#54C0FC' : '#f74343')};
	color: white;
`;

const UploadButton = styled(Button)`
	width: fit-content;
	margin-bottom: 20px;
`;

export default ImportsTable;
